import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { RoomView } from '@kidzim/shared';
import { Badge, Button, Card, ErrorState } from '../design-system/components';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';

export function Overview() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState<RoomView[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);

  function load() {
    setLoadFailed(false);
    api<{ rooms: RoomView[] }>('/api/rooms')
      .then((r) => setRooms(r.rooms))
      .catch(() => setLoadFailed(true));
  }
  useEffect(() => {
    load();
  }, []);

  return (
    <div className="kz-stack" style={{ gap: 'var(--space-6)' }}>
      <div>
        <span className="kz-eyebrow">Overview</span>
        <h1 style={{ marginTop: 'var(--space-1)' }}>Hi {user?.displayName} 👋</h1>
      </div>

      <Card style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', background: 'var(--safe-50)', borderColor: 'var(--safe-100)' }}>
        <span style={{ fontSize: 28 }}>🛡️</span>
        <div>
          <h3 style={{ color: 'var(--safe-700)' }}>Everything looks safe</h3>
          <p className="kz-muted">Every chat is supervised and saved for you to read any time.</p>
        </div>
      </Card>

      <div className="kz-row" style={{ justifyContent: 'space-between' }}>
        <h2>Your chat rooms</h2>
        <Link to="/app/rooms">
          <Button>+ New room</Button>
        </Link>
      </div>

      {loadFailed ? (
        <ErrorState message="Couldn't load your chat rooms." onRetry={load} />
      ) : rooms.length === 0 ? (
        <Card>
          <p className="kz-muted">No rooms yet. Create one to invite your kids and their buddies.</p>
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 'var(--space-4)' }}>
          {rooms.slice(0, 6).map((room) => (
            <Link key={room.id} to={`/app/rooms/${room.id}`} style={{ textDecoration: 'none' }}>
              <Card interactive>
                <div className="kz-row" style={{ justifyContent: 'space-between' }}>
                  <h3 style={{ font: 'var(--type-h3)' }}>{room.name}</h3>
                  <RoomStatusBadge status={room.status} />
                </div>
                <p className="kz-muted" style={{ marginTop: 'var(--space-2)', font: 'var(--type-small)' }}>
                  {room.permanent ? 'Always open' : room.opensAt ? `Opens ${new Date(room.opensAt).toLocaleString()}` : ''}
                </p>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function RoomStatusBadge({ status }: { status: RoomView['status'] }) {
  if (status === 'open') return <Badge tone="safe">Open</Badge>;
  if (status === 'scheduled') return <Badge tone="warn">Scheduled</Badge>;
  return <Badge tone="neutral">Closed</Badge>;
}
